import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { User, BookOpen, GraduationCap } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TutorCard = ({ tutor, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      whileHover={{ y: -8 }}
      className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
    >
      <div className="flex flex-col sm:flex-row gap-6">
        {/* Profile Picture */}
        <div className="flex-shrink-0">
          {tutor.avatar_url ? (
            <img
              src={tutor.avatar_url}
              alt={tutor.name}
              className="w-24 h-24 sm:w-32 sm:h-32 rounded-2xl object-cover border-4 border-cyan-100"
            />
          ) : (
            <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center border-4 border-cyan-100">
              <User className="w-12 h-12 sm:w-16 sm:h-16 text-white" />
            </div>
          )}
        </div>

        {/* Tutor Details */}
        <div className="flex-1 min-w-0">
          {/* Name */}
          <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
            {tutor.name}
          </h3>

          {/* Qualification */}
          {tutor.qualification && (
            <div className="flex items-center gap-2 text-cyan-600 mb-3">
              <GraduationCap className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm sm:text-base font-medium">{tutor.qualification}</span>
            </div>
          )}

          {/* Subjects */}
          {tutor.subjects && tutor.subjects.length > 0 && (
            <div className="flex items-start gap-2 mb-3">
              <BookOpen className="w-4 h-4 flex-shrink-0 text-gray-400 mt-1" />
              <div className="flex flex-wrap gap-2">
                {tutor.subjects.map((subject, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 bg-gradient-to-r from-cyan-50 to-blue-50 text-cyan-700 text-xs sm:text-sm rounded-full border border-cyan-200 font-medium"
                  >
                    {subject}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Bio / Description */}
          {tutor.bio && (
            <p className="text-gray-600 text-sm sm:text-base leading-relaxed line-clamp-3">
              {tutor.bio}
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const Tutors = () => {
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTutors = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/api/tutors/public`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch tutors');
        }
        
        const data = await response.json();
        setTutors(data);
      } catch (err) {
        console.error('Error fetching tutors:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchTutors();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="pt-12 pb-8 sm:pt-16 sm:pb-12 bg-gradient-to-br from-cyan-600 via-blue-600 to-teal-600 relative overflow-hidden">
          <div className="absolute inset-0 overflow-hidden opacity-20">
            <motion.div
              className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full filter blur-3xl"
              animate={{
                scale: [1, 1.2, 1],
                x: [0, 50, 0],
              }}
              transition={{
                duration: 10,
                repeat: Infinity,
              }}
            />
          </div>

          <div className="container mx-auto px-6 lg:px-12 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4">
                Meet Our <span className="text-cyan-200">Expert Tutors</span>
              </h1>
              <p className="text-cyan-100 text-base sm:text-lg max-w-2xl mx-auto">
                Learn from experienced educators who are passionate about helping students succeed.
                Our tutors bring expertise across various subjects and curricula.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Tutors List Section */}
        <section className="py-16 sm:py-20 bg-gradient-to-br from-cyan-50 to-blue-50">
          <div className="container mx-auto px-6 lg:px-12">
            <div className="max-w-5xl mx-auto">
              {loading ? (
                <div className="flex justify-center items-center py-20">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-600"></div>
                </div>
              ) : error ? (
                <div className="text-center py-20 bg-white rounded-2xl shadow-lg">
                  <p className="text-red-500 text-lg">{error}</p>
                  <p className="text-gray-500 mt-2">Please try again later.</p>
                </div>
              ) : tutors.length === 0 ? (
                <div className="text-center py-20 bg-white rounded-2xl shadow-lg">
                  <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600 text-lg">No tutors available at the moment.</p>
                  <p className="text-gray-500 mt-2">Check back soon!</p>
                </div>
              ) : (
                <div className="grid gap-6">
                  {tutors.map((tutor, index) => (
                    <TutorCard key={tutor.id} tutor={tutor} index={index} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Tutors;
